// Defaults & Workout rebuild — the DW-M1 "Monthly Default & Workout
// Status" card on the Defaults tab (the series ALSO appears on the
// Financial Reporting tab automatically — same machinery, same instances).
// Preparer path rides the DW-scoped endpoints (Defaults.Manage — B4 holds
// no Loans.Manage); reviewer signature stays FinancialReporting.Review.
// Clean loans get the one-click create; the narrative modal covers the
// active-case "status and next steps".

import { useMemo, useState } from "react";
import { Badge, Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import type { LoanReportDto } from "@/api/portfolioAdmin/loanReportsApi";
import { DW_SERIES_CODE } from "@/api/portfolioAdmin/financialReportingApi";
import {
  useCertifyReportInstance,
  useCreateDwReportInstance,
  useFileReportInstance,
  useLoanReportingOverview,
  useSubmitDwReportInstance,
} from "@/hooks/portfolioAdmin/useFinancialReporting";
import { CreateReportModal } from "@/components/portfolioAdmin/reporting/CreateReportModal";
import { SeriesReportPreviewDrawer } from "@/components/portfolioAdmin/reporting/SeriesReportPreviewDrawer";
import { LoanReportStatusBadge } from "@/components/portfolioAdmin/loanDetail/reports/LoanReportLedger";
import { reportingPeriodLabel } from "@/components/portfolioAdmin/reporting/reportingFormat";
import { formatCalendarDate } from "@/utils/datetime";

const DUE_BADGE: Record<string, "draft" | "review" | "error"> = {
  upcoming: "draft",
  due: "review",
  overdue: "error",
};

export function DwReportCard({ loanId }: { loanId: string }) {
  const auth = useAuth();
  const toast = useToast();
  const overview = useLoanReportingOverview(loanId);

  const create = useCreateDwReportInstance(loanId);
  const submit = useSubmitDwReportInstance(loanId);
  const certify = useCertifyReportInstance(loanId);
  const file = useFileReportInstance(loanId);

  const [createOpen, setCreateOpen] = useState(false);
  const [openReport, setOpenReport] = useState<LoanReportDto | null>(null);

  const canPrepare =
    auth.hasPermission("Defaults.Manage") || auth.hasRoleCode("ADMIN");
  const canReview =
    auth.hasPermission("FinancialReporting.Review") || auth.hasRoleCode("ADMIN");

  const card = useMemo(
    () => overview.data?.series.find((s) => s.code === DW_SERIES_CODE) ?? null,
    [overview.data],
  );
  const instances = useMemo(
    () =>
      (overview.data?.instances ?? []).filter((i) => i.seriesCode === DW_SERIES_CODE),
    [overview.data],
  );

  if (overview.isLoading) {
    return (
      <div
        className="border border-border-light rounded-md bg-white-pure p-space-3"
        data-testid="dw-report-card"
      >
        <p className="text-small text-gray-mid italic m-0">Loading report status…</p>
      </div>
    );
  }
  // Series not yet active/seeded in this environment — render nothing
  // rather than a broken card.
  if (!card) return null;

  const doCreate = async (narrative: string | null) => {
    try {
      const draft = await create.mutateAsync({
        periodStart: card.nextPeriodStart,
        periodEnd: card.nextPeriodEnd,
        narrative,
      });
      setCreateOpen(false);
      setOpenReport(draft);
    } catch (err) {
      // The modal shows its own error; the one-click path needs a toast.
      if (!createOpen) toast.error(extractErrorMessage(err));
      throw err;
    }
  };

  return (
    <div
      className="border border-border-light rounded-md bg-white-pure p-space-3 flex flex-col gap-space-2"
      data-testid="dw-report-card"
    >
      {/* Title + badge on one row, actions on their own wrapping row (the
          SeriesCard layout) — badge + two buttons crowded into the header
          overflowed the side rail at narrow widths. */}
      <div className="flex items-start justify-between gap-space-2">
        <div className="min-w-0">
          <h3
            className="font-display font-bold text-h4 m-0 truncate"
            title="Monthly status report (DW-M1)"
          >
            Monthly status report (DW-M1)
          </h3>
          <p className="m-0 text-caption text-gray-mid">
            {reportingPeriodLabel(card.cadence, card.nextPeriodEnd)} • due{" "}
            {formatCalendarDate(card.nextDueDate)}
          </p>
        </div>
        <span className="shrink-0">
          <Badge status={DUE_BADGE[card.dueStatus] ?? "draft"}>{card.dueStatus}</Badge>
        </span>
      </div>
      {canPrepare && (
        <div className="flex flex-wrap items-center gap-space-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => doCreate(null).catch(() => undefined)}
            disabled={create.isPending}
            data-testid="dw-report-create"
          >
            {create.isPending ? "Creating…" : "Create report"}
          </Button>
          <Button
            variant="text"
            size="sm"
            onClick={() => setCreateOpen(true)}
            data-testid="dw-report-create-with-narrative"
          >
            With narrative…
          </Button>
        </div>
      )}

      {instances.length === 0 ? (
        <p className="m-0 text-small text-gray-mid italic">
          No DW-M1 reports filed yet. For performing loans this is the
          one-click clean bill of health.
        </p>
      ) : (
        <ul className="m-0 p-0 list-none" data-testid="dw-report-ledger">
          {instances.map((i) => (
            <li key={i.id} className="border-b border-border-light last:border-0">
              <button
                type="button"
                onClick={() => setOpenReport(i)}
                className="w-full text-left py-space-2 flex items-center gap-space-3 hover:bg-white-warm rounded"
                data-testid={`dw-report-row-${i.id}`}
              >
                <span className="text-small font-bold">
                  {reportingPeriodLabel("Monthly", i.periodEnd)}
                </span>
                <LoanReportStatusBadge status={i.status} />
                <span className="text-caption text-gray-mid ml-auto">
                  {i.preparedByUserName ?? ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {createOpen && (
        <CreateReportModal
          card={card}
          open
          onClose={() => setCreateOpen(false)}
          onCreate={doCreate}
          isPending={create.isPending}
        />
      )}

      {openReport && (
        <SeriesReportPreviewDrawer
          report={openReport}
          seriesTitle={card.title}
          open
          onClose={() => setOpenReport(null)}
          canPrepare={canPrepare}
          canReview={canReview}
          onSubmit={async (id) => setOpenReport(await submit.mutateAsync(id))}
          onCertify={async (id) => setOpenReport(await certify.mutateAsync(id))}
          onFile={async (id) => setOpenReport(await file.mutateAsync(id))}
          isPending={submit.isPending || certify.isPending || file.isPending}
        />
      )}
    </div>
  );
}
